import { useCallback, useEffect, useState } from 'react';
import { getOnboardingState } from './api';
import { shouldShowFrontDoorCards } from './frontDoorCards';

/**
 * E7 FRONT DOOR — the "should the 7 profile-facts cards show at all" gate.
 * docs/diagrams/c4-e7-front-door-2026-07-17.html. Mirrors apps/web's App.tsx
 * OnboardingGate (that file's own doc comment: "Checks GET /api/profile/
 * onboarding EXACTLY ONCE per authenticated app mount... A thin,
 * never-onboarded account gets redirected to /welcome; every other account —
 * and any error resolving the check — is left exactly where it was. Fails
 * CLOSED toward NOT showing onboarding") — same fetch-once-per-mount shape,
 * same fail-closed posture, adapted from "redirect to a route" (apps/web has
 * a real /welcome page) to "flip a boolean an overlay renders on" (this app
 * has no route for the flow — it composes into the SAME absolutely-
 * positioned overlay slot the existing corpus-discovery arc already uses;
 * see components/FrontDoorCards.tsx + App.tsx's own composition comment).
 *
 * Deliberately separate from lib/useFrontDoorCards.ts (the thin data hook
 * FrontDoorCards.tsx uses once mounted for the actual profile reads/writes)
 * — this hook's ONE job is the gate decision, so App.tsx can compute
 * showFrontDoorCards (needed for tabBar/coachMark/homeRouteGate composition)
 * without needing to mount the full card-flow component tree first. Mirrors
 * apps/web's own precedent of two independent GET /profile/onboarding calls
 * (the App.tsx gate check, and FrontDoor.tsx's own useOnboarding read) rather
 * than threading one fetch through two disjoint concerns.
 */
export function useFrontDoorGate(enabled: boolean, preview: boolean) {
  const [eligibleForOnboarding, setEligibleForOnboarding] = useState<boolean | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    getOnboardingState()
      .then((state) => {
        if (alive) setEligibleForOnboarding(state.eligibleForOnboarding);
      })
      .catch(() => {
        // Fail closed — see the doc comment above. eligibleForOnboarding
        // stays null, which shouldShowFrontDoorCards treats as "don't show"
        // outside preview.
      });
    return () => {
      alive = false;
    };
    // Intentionally keyed on `enabled` alone (mirrors useOnboardingGate.ts's
    // own refreshCorpus effect and App.tsx's existing OnboardingGate empty-
    // deps discipline): this must check ONCE per became-enabled transition,
    // never re-fire on unrelated re-renders — a user who finishes or
    // deliberately skips the cards must never be re-trapped by a stale
    // re-check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const markFinished = useCallback(() => setFinished(true), []);

  const showCards = shouldShowFrontDoorCards({ eligibleForOnboarding, preview, finished });

  return { eligibleForOnboarding, showCards, finished, markFinished };
}
